/**
 * DataBest — PerfKpiCards (MVP redesign)
 * 4 cards: Caregiver Score · Total Records (breakdown) · Coverage Gap · Report Quality.
 */
import React from "react";
import { Award, ClipboardList, PieChart, Star } from "lucide-react";

const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

const PerfKpiCards = ({ kpis }) => {
    const completed = kpis.records_completed ?? kpis.total_measurements ?? 0;
    const onTime = kpis.on_time_count ?? Math.max(0, completed - (kpis.backlog_completions ?? 0));
    const notOnTime = kpis.not_on_time_count ?? kpis.backlog_completions ?? 0;
    const notCompleted = kpis.records_not_completed ?? kpis.missed_tasks ?? 0;

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {/* Caregiver Score */}
            <div
                data-testid="perf-kpi-score"
                className="rounded-2xl border border-brand-blue-200 bg-brand-blue-50/40 p-4 shadow-sm"
            >
                <div className="flex items-start justify-between">
                    <div className="rounded-lg bg-brand-blue-100 p-2">
                        <Star className="h-4 w-4 text-brand-blue-700" />
                    </div>
                </div>
                <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Caregiver Score
                </p>
                <p className="mt-0.5 font-heading text-2xl font-bold text-brand-blue-950 tabular-nums">
                    {kpis.caregiver_score !== undefined && kpis.caregiver_score !== null
                        ? `${kpis.caregiver_score}%`
                        : "—"}
                </p>
                <p className="mt-1 text-[10px] leading-tight text-slate-500">
                    (%Records Completed + %On-time + %Report Quality + (100% − %Coverage Gap)) / 4
                </p>
            </div>

            {/* Total Records */}
            <div
                data-testid="perf-kpi-total-records"
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
                <div className="flex items-start justify-between">
                    <div className="rounded-lg bg-slate-100 p-2">
                        <ClipboardList className="h-4 w-4 text-slate-700" />
                    </div>
                </div>
                <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Total Records
                </p>
                <p className="mt-0.5 font-heading text-2xl font-bold text-slate-900 tabular-nums">
                    {kpis.total_records ?? completed + notCompleted}
                </p>
                <div className="mt-1 space-y-0.5 text-[11px] text-slate-600">
                    <p className="font-semibold">
                        Records Completed: <span className="tabular-nums">{completed}</span>
                    </p>
                    <p className="pl-2 text-[10px]">
                        • On-time = <span className="tabular-nums">{onTime}</span>
                    </p>
                    <p className="pl-2 text-[10px]">
                        • Not On-time = <span className="tabular-nums">{notOnTime}</span>
                    </p>
                    <p className="font-semibold">
                        Records Not Completed:{" "}
                        <span className="tabular-nums">{notCompleted}</span>
                    </p>
                </div>
            </div>

            {/* Coverage Gap */}
            <div
                data-testid="perf-kpi-coverage-gap"
                className={`rounded-2xl border p-4 shadow-sm ${
                    (kpis.coverage_gap_pct ?? 0) > 30
                        ? "border-amber-200 bg-amber-50/40"
                        : "border-slate-200 bg-white"
                }`}
            >
                <div className="flex items-start justify-between">
                    <div className="rounded-lg bg-amber-100 p-2">
                        <PieChart className="h-4 w-4 text-amber-700" />
                    </div>
                </div>
                <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Coverage Gap
                </p>
                <p className="mt-0.5 font-heading text-2xl font-bold text-slate-900 tabular-nums">
                    {pct(kpis.coverage_gap_pct)}
                </p>
                <p className="mt-1 text-[11px] font-semibold text-slate-500">
                    (% Days Not Rated)
                </p>
            </div>

            {/* Report Quality */}
            <div
                data-testid="perf-kpi-report-quality"
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
                <div className="flex items-start justify-between">
                    <div className="rounded-lg bg-brand-green-100 p-2">
                        <Award className="h-4 w-4 text-brand-green-700" />
                    </div>
                </div>
                <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Report Quality
                </p>
                <p className="mt-0.5 font-heading text-2xl font-bold text-slate-900 tabular-nums">
                    {kpis.report_quality_pct !== null && kpis.report_quality_pct !== undefined
                        ? `${kpis.report_quality_pct}%`
                        : "—"}
                </p>
                <p className="mt-1 text-[11px] font-semibold text-slate-500">
                    {kpis.letter_grade ? `Letter grade: ${kpis.letter_grade}` : "No measurements yet"}
                </p>
            </div>
        </div>
    );
};

export default PerfKpiCards;
